import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useManagementProjectsLite } from '@/hooks/useManagementProjectsLite';
import FileUploader, { type UploadedFileRecord } from './FileUploader';
import AssignSubcontractorPanel from './AssignSubcontractorPanel';
import {
  emitInstructionsSync,
  unlinkInstructionAttachment,
  type ManagementInstructionRecord,
} from '@/lib/instructionUtils';

interface SubcontractorOption {
  id: string;
  full_name: string | null;
  company_name: string | null;
  email: string | null;
}

interface ProjectOption {
  id: string;
  project_name: string;
  project_code: string;
  site_address: string | null;
  status: string;
  client_user_id: string | null;
  client_name: string | null;
}

type RecipientType = 'internal' | 'subcontractor';

interface Props {
  show: boolean;
  onClose: () => void;
  projectId: string;
  linkedScheduleItemId?: string;
  linkedChecklistItemId?: string;
  linkedPhotoId?: string;
  defaultTitle?: string;
  onSuccess?: () => void;
  allowProjectChange?: boolean;
  source?: string;
  editingInstruction?: ManagementInstructionRecord | null;
}

async function fetchProjectWithClient(projectId: string): Promise<ProjectOption | null> {
  const { data: proj, error: projErr } = await supabase
    .from('jphph_projects')
    .select('id, project_name, site_address, status')
    .eq('id', projectId)
    .maybeSingle();
  if (projErr || !proj) {
    console.error('[NewInstructionModal] project load error:', projErr?.message);
    return null;
  }
  const { data: members } = await supabase
    .from('jphph_project_members')
    .select('user_id')
    .eq('project_id', projectId)
    .eq('member_role', 'client')
    .limit(1);
  const clientUserId = members && members.length > 0 ? (members[0].user_id as string) : null;
  let clientName: string | null = null;
  if (clientUserId) {
    const { data: profile } = await supabase
      .from('jphph_profiles')
      .select('full_name, company_name')
      .eq('id', clientUserId)
      .maybeSingle();
    clientName = (profile?.full_name as string | null) || (profile?.company_name as string | null) || null;
  }
  return {
    id: proj.id,
    project_name: proj.project_name as string,
    project_code: proj.id.slice(0, 6).toUpperCase(),
    site_address: (proj.site_address as string | null) || null,
    status: (proj.status as string) || 'active',
    client_user_id: clientUserId,
    client_name: clientName,
  };
}

async function loadSubcontractorsForProject(projectId: string): Promise<SubcontractorOption[]> {
  console.log('[NewInstructionModal] loading subcontractors for project:', projectId);

  // Tier 1: Edge function
  try {
    const { data: efData, error: efErr } = await supabase.functions.invoke('jphph-subcontractor-assignments', {
      body: { action: 'list', project_id: projectId },
    });
    console.log('[NewInstructionModal] edge function raw response:', { efData, efErr });
    if (!efErr && efData && typeof efData === 'object') {
      const subsArr = (efData as Record<string, unknown>).subcontractors;
      if (Array.isArray(subsArr)) {
        console.log('[NewInstructionModal] subcontractors loaded via EF:', subsArr.length);
        return subsArr as SubcontractorOption[];
      }
    }
  } catch (efFail) {
    console.warn('[NewInstructionModal] edge function failed:', efFail);
  }

  // Tier 2: Local query
  try {
    const { data: members, error: memberErr } = await supabase
      .from('jphph_project_members')
      .select('user_id')
      .eq('project_id', projectId)
      .eq('member_role', 'subcontractor');
    if (memberErr) throw memberErr;
    const userIds = (members || []).map((m) => m.user_id as string);
    if (userIds.length > 0) {
      const { data: profiles, error: profileErr } = await supabase
        .from('jphph_profiles')
        .select('id, full_name, company_name, email')
        .in('id', userIds);
      if (profileErr) throw profileErr;
      const subs = (profiles || []) as SubcontractorOption[];
      console.log('[NewInstructionModal] subcontractors loaded via local query:', subs.length);
      return subs;
    }
  } catch (localErr) {
    console.warn('[NewInstructionModal] local query failed:', localErr);
  }

  // Tier 3: Raw HTTP fetch
  try {
    const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/jphph-subcontractor-assignments`;
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    const anonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string | undefined;
    if (token && anonKey) {
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: anonKey,
        },
        body: JSON.stringify({ action: 'list', project_id: projectId }),
      });
      const rawData = await resp.json().catch(() => null);
      if (rawData && Array.isArray(rawData.subcontractors)) {
        console.log('[NewInstructionModal] subcontractors loaded via raw fetch:', rawData.subcontractors.length);
        return rawData.subcontractors as SubcontractorOption[];
      }
    }
  } catch (rawErr) {
    console.warn('[NewInstructionModal] raw fetch failed:', rawErr);
  }

  console.log('[NewInstructionModal] no subcontractors found after all tiers');
  return [];
}

export default function NewInstructionModal({
  show, onClose, projectId: initialProjectId, linkedScheduleItemId,
  linkedChecklistItemId, linkedPhotoId, defaultTitle, onSuccess, allowProjectChange = false, source = 'unknown',
  editingInstruction = null,
}: Props) {
  const { user } = useAuth();
  const { projects: selectProjects, loading: selectProjectsLoading, error: selectProjectsError, refresh: refreshSelectProjects } = useManagementProjectsLite();

  const [selectedProjectId, setSelectedProjectId] = useState(initialProjectId || '');
  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);
  const [projectLoading, setProjectLoading] = useState(false);

  const [subcontractors, setSubcontractors] = useState<SubcontractorOption[]>([]);
  const [subcontractorsLoading, setSubcontractorsLoading] = useState(false);
  const [showAssignPanel, setShowAssignPanel] = useState(false);

  const [recipientType, setRecipientType] = useState<RecipientType>('subcontractor');
  const [toUserId, setToUserId] = useState('');

  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState('');
  const [title, setTitle] = useState(defaultTitle || '');
  const [body, setBody] = useState('');

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileRecord[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<UploadedFileRecord[]>([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<string[]>([]);
  const [showFileUploader, setShowFileUploader] = useState(false);
  const [photoType, setPhotoType] = useState<'before' | 'after' | 'reference' | 'attachment'>('before');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Debug: track hook state changes
  useEffect(() => {
    console.log('[Instruction Project Select] modal observes hook state', {
      selectProjectsCount: selectProjects.length,
      selectProjectsLoading,
      selectProjectsError,
      selectedProjectId,
      allowProjectChange,
      show,
    });
  }, [selectProjects.length, selectProjectsLoading, selectProjectsError, selectedProjectId, allowProjectChange, show]);

  // ── State reset on open / close ──
  useEffect(() => {
    if (!show) {
      resetForm();
      return;
    }

    if (editingInstruction) {
      setTitle(editingInstruction.instruction_title);
      setBody(editingInstruction.body);
      setDueDate(editingInstruction.due_at ? editingInstruction.due_at.split('T')[0] : '');
      setPriority(editingInstruction.priority || 'medium');
      setRecipientType(editingInstruction.to_user_id ? 'subcontractor' : 'internal');
      setToUserId(editingInstruction.to_user_id || '');
      setSelectedProjectId(editingInstruction.project_id);
      setUploadedFiles([]);
      setRemovedAttachmentIds([]);
      setError('');

      supabase
        .from('jphph_project_files')
        .select('id, file_name, file_url, file_type, photo_type, caption')
        .eq('instruction_id', editingInstruction.id)
        .eq('is_deleted', false)
        .then(({ data }) => {
          setExistingAttachments(
            (data || []).map((f) => ({
              id: f.id as string,
              file_name: f.file_name as string,
              file_url: f.file_url as string,
              file_type: (f.file_type as string | null) || null,
              photo_type: (f.photo_type as string | null) || null,
              caption: (f.caption as string | null) || null,
            }))
          );
        });
      return;
    }

    // Modal opened — reset all editable fields to defaults
    console.log('NewInstructionModal opened', {
      source,
      initialProjectId,
      selectedProjectId: initialProjectId || '',
    });
    setTitle(defaultTitle || '');
    setBody('');
    setDueDate('');
    setPriority('medium');
    setToUserId('');
    setUploadedFiles([]);
    setExistingAttachments([]);
    setRemovedAttachmentIds([]);
    setError('');
    setRecipientType('subcontractor');
    setShowAssignPanel(false);
    setSubcontractors([]);
    setSubcontractorsLoading(false);
    if (initialProjectId) {
      setSelectedProjectId(initialProjectId);
    } else {
      setSelectedProjectId('');
      setSelectedProject(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, editingInstruction?.id]);

  // Sync selected project ID when prop changes
  useEffect(() => {
    if (initialProjectId) setSelectedProjectId(initialProjectId);
  }, [initialProjectId]);

  // Load single project when NOT allowed to change
  useEffect(() => {
    if (!show || !selectedProjectId || allowProjectChange) return;
    let cancelled = false;
    setProjectLoading(true);
    fetchProjectWithClient(selectedProjectId).then((proj) => {
      if (cancelled) return;
      if (proj) setSelectedProject(proj);
      setProjectLoading(false);
      console.log('[NewInstructionModal] single project loaded', {
        projectId: selectedProjectId,
        loaded: !!proj,
        name: proj?.project_name,
      });
    });
    return () => { cancelled = true; };
  }, [show, selectedProjectId, allowProjectChange]);

  // When allowProjectChange is true, map from hook results
  useEffect(() => {
    if (!show || !allowProjectChange) return;
    if (selectProjects.length === 0) return;
    const match = selectProjects.find((p) => p.id === selectedProjectId);
    if (match) {
      const projectOption: ProjectOption = {
        id: match.id,
        project_name: match.project_name,
        project_code: match.id.slice(0, 6).toUpperCase(),
        site_address: match.site_address,
        status: match.status || 'active',
        client_user_id: null,
        client_name: null,
      };
      setSelectedProject(projectOption);
      console.log('[NewInstructionModal] selected project mapped from hook', {
        selectedProjectId,
        name: projectOption.project_name,
      });
      // Load client info for this project
      let cancelled = false;
      (async () => {
        const { data: members } = await supabase
          .from('jphph_project_members')
          .select('user_id')
          .eq('project_id', match.id)
          .eq('member_role', 'client')
          .limit(1);
        if (cancelled) return;
        const clientUserId = members && members.length > 0 ? (members[0].user_id as string) : null;
        let clientName: string | null = null;
        if (clientUserId) {
          const { data: profile } = await supabase
            .from('jphph_profiles')
            .select('full_name, company_name')
            .eq('id', clientUserId)
            .maybeSingle();
          if (cancelled) return;
          clientName = (profile?.full_name as string | null) || (profile?.company_name as string | null) || null;
        }
        if (!cancelled) {
          setSelectedProject((prev) => prev ? { ...prev, client_user_id: clientUserId, client_name: clientName } : prev);
        }
      })();
      return () => { cancelled = true; };
    } else if (!selectedProjectId) {
      setSelectedProject(null);
    }
  }, [show, allowProjectChange, selectProjects, selectedProjectId]);

  // Load subcontractors when project changes
  useEffect(() => {
    if (!show || !selectedProjectId) return;
    let cancelled = false;
    setSubcontractorsLoading(true);
    loadSubcontractorsForProject(selectedProjectId).then((subs) => {
      if (cancelled) return;
      setSubcontractors(subs);
      if (editingInstruction && editingInstruction.project_id === selectedProjectId) {
        setToUserId(editingInstruction.to_user_id || '');
      } else if (subs.length === 1) {
        setToUserId(subs[0].id);
      } else if (!editingInstruction) {
        setToUserId('');
      }
      setSubcontractorsLoading(false);
    });
    return () => { cancelled = true; };
  }, [show, selectedProjectId, editingInstruction]);

  useEffect(() => {
    if (defaultTitle) setTitle(defaultTitle);
  }, [defaultTitle]);

  const handleFileUploadComplete = useCallback((records: UploadedFileRecord[]) => {
    setUploadedFiles((prev) => [...prev, ...records]);
    setShowFileUploader(false);
  }, []);

  const isFormValid = useMemo(() => {
    if (!selectedProjectId || !title.trim() || !body.trim()) return false;
    if (recipientType === 'subcontractor' && !toUserId) return false;
    return true;
  }, [selectedProjectId, title, body, recipientType, toUserId]);

  const getValidationMessage = useMemo(() => {
    if (!selectedProjectId) return 'Please select a project first.';
    if (!title.trim()) return 'Enter a title.';
    if (!body.trim()) return 'Enter a message.';
    if (recipientType === 'subcontractor' && !toUserId) {
      if (subcontractors.length === 0) return 'Assign and select a subcontractor before sending.';
      return 'Select a subcontractor recipient.';
    }
    return '';
  }, [selectedProjectId, title, body, recipientType, toUserId, subcontractors.length]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    console.log('[NewInstructionModal] submit attempted', {
      projectId: selectedProjectId,
      type: 'instruction',
      targetRecipient: recipientType,
      title: title.trim(),
      isFormValid,
    });

    if (!user || !isFormValid || !selectedProjectId) {
      setError(getValidationMessage || 'Please fill in all required fields.');
      return;
    }

    setSaving(true);
    setError('');

    if (editingInstruction) {
      const updatePayload = {
        instruction_title: title.trim(),
        body: body.trim(),
        priority,
        due_at: dueDate ? new Date(dueDate).toISOString() : null,
        to_user_id: recipientType === 'subcontractor' ? (toUserId || null) : null,
        assigned_to: recipientType === 'subcontractor' ? (toUserId || null) : null,
        requires_response: recipientType === 'subcontractor' && !!toUserId,
      };

      const { error: updateErr } = await supabase
        .from('jphph_instructions')
        .update(updatePayload)
        .eq('id', editingInstruction.id);

      if (updateErr) {
        setError(updateErr.message);
        setSaving(false);
        return;
      }

      for (const fileId of removedAttachmentIds) {
        await unlinkInstructionAttachment(fileId);
      }

      if (uploadedFiles.length > 0) {
        for (const f of uploadedFiles) {
          await supabase
            .from('jphph_project_files')
            .update({ instruction_id: editingInstruction.id, photo_type: photoType })
            .eq('id', f.id);
        }
      }

      setSaving(false);
      resetForm();
      handleClose();
      emitInstructionsSync(editingInstruction.project_id);
      onSuccess?.();
      return;
    }

    const targetRole = recipientType === 'subcontractor' ? 'subcontractor' : 'internal';
    const instrStatus = recipientType === 'internal' ? 'closed' : 'sent';

    const payload = {
      project_id: selectedProjectId,
      from_user_id: user.id,
      to_user_id: toUserId || null,
      instruction_title: title.trim(),
      body: body.trim(),
      priority,
      status: instrStatus,
      requires_response: recipientType === 'subcontractor' && !!toUserId,
      due_at: dueDate ? new Date(dueDate).toISOString() : null,
      linked_schedule_item_id: linkedScheduleItemId || null,
      linked_checklist_item_id: linkedChecklistItemId || null,
      linked_photo_id: linkedPhotoId || null,
      instruction_type: 'instruction',
      assigned_to: toUserId || null,
      is_shared_with_client: false,
    };

    console.log('Instruction submit payload', payload);

    const { data: inserted, error: insertErr } = await supabase
      .from('jphph_instructions')
      .insert(payload)
      .select()
      .maybeSingle();

    if (insertErr) {
      console.error('[NewInstructionModal] insert error:', insertErr);
      setError(insertErr.message);
      setSaving(false);
      return;
    }

    console.log('[NewInstructionModal] instruction inserted', { id: inserted?.id });

    // Link files
    if (inserted && uploadedFiles.length > 0) {
      for (const f of uploadedFiles) {
        await supabase.from('jphph_project_files')
          .update({ instruction_id: inserted.id, photo_type: photoType })
          .eq('id', f.id);
      }
    }

    // Notification for subcontractor
    if (inserted && toUserId && recipientType === 'subcontractor') {
      await supabase.from('jphph_notifications').insert({
        user_id: toUserId,
        project_id: selectedProjectId,
        notification_type: 'instruction',
        title: `New instruction: ${title.trim()}`,
        body: body.trim().slice(0, 200),
        target_role: 'subcontractor',
        link_path: '/subcontractor/instructions',
        related_table: 'jphph_instructions',
        related_id: inserted.id,
        is_read: false,
      });
    }

    setSaving(false);
    resetForm();
    handleClose();
    emitInstructionsSync(selectedProjectId);
    onSuccess?.();
  }, [
    user, title, body, toUserId, priority, dueDate, selectedProjectId,
    linkedScheduleItemId, linkedChecklistItemId, linkedPhotoId,
    uploadedFiles, photoType, handleClose, onSuccess, isFormValid, getValidationMessage, recipientType,
    editingInstruction, removedAttachmentIds,
  ]);

  function resetForm() {
    setTitle('');
    setBody('');
    setDueDate('');
    setPriority('medium');
    setToUserId('');
    setUploadedFiles([]);
    setExistingAttachments([]);
    setRemovedAttachmentIds([]);
    setError('');
    setRecipientType('subcontractor');
    setShowAssignPanel(false);
    if (!initialProjectId) setSelectedProjectId('');
  }

  if (!show) return null;

  const senderName = user?.user_metadata?.full_name || 'Management';

  const getRecipientContextDisplay = (): string => {
    if (recipientType === 'internal') return 'Internal only';
    if (toUserId) {
      const sub = subcontractors.find((s) => s.id === toUserId);
      return sub ? `Subcontractor: ${sub.full_name || sub.company_name || sub.email || 'Unknown'}` : 'Subcontractor not selected';
    }
    return 'Subcontractor not selected';
  };

  const getStatusContextDisplay = (): string => {
    if (recipientType === 'internal') return 'Draft';
    if (toUserId) return 'Ready to send';
    return 'Cannot send yet';
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end lg:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose}></div>
      <div className="relative bg-white w-full max-w-full lg:max-w-lg rounded-t-3xl lg:rounded-3xl overflow-hidden max-h-[92vh] flex flex-col" style={{ boxSizing: 'border-box' }}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <h3 className="text-[#1A1A1A] font-bold text-base">
            {editingInstruction ? 'Edit Instruction' : 'New Instruction'}
          </h3>
          <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-[#374151]"></i>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-h-0">
          {/* Project & Recipients */}
          <div className="space-y-3">
            <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">Project & Recipients</p>

            {/* Project Selector */}
            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">Project *</label>
              {allowProjectChange ? (
                <div className="space-y-1">
                  <select
                    value={selectedProjectId}
                    onChange={(e) => {
                      const newId = e.target.value;
                      const match = selectProjects.find((p) => p.id === newId);
                      console.log('Project selected in modal', {
                        selectedProjectId: newId,
                        selectedProject: match || null,
                      });
                      setSelectedProjectId(newId);
                    }}
                    disabled={selectProjectsLoading}
                    className="w-full min-w-0 max-w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] cursor-pointer disabled:opacity-60"
                    style={{ boxSizing: 'border-box' }}
                  >
                    <option value="">Select a project...</option>
                    {selectProjects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.project_name}{p.site_address ? ` — ${p.site_address}` : ''}
                      </option>
                    ))}
                  </select>
                  {selectProjectsLoading && (
                    <div className="flex items-center gap-1.5 text-[#9CA3AF] text-[10px]">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Loading projects...
                    </div>
                  )}
                  {selectProjectsError && (
                    <div className="flex items-center gap-1.5 text-[#EF4444] text-[10px]">
                      <i className="ri-error-warning-line"></i>
                      {selectProjectsError}
                      <button onClick={refreshSelectProjects} className="underline cursor-pointer whitespace-nowrap">Retry</button>
                    </div>
                  )}
                  {selectProjects.length === 0 && !selectProjectsLoading && !selectProjectsError && (
                    <div className="flex items-center gap-1.5 text-[#9CA3AF] text-[10px]">
                      <i className="ri-information-line"></i>
                      No projects found. If projects exist, check access permissions.
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 bg-[#F9FAFB] rounded-xl px-4 py-3 border border-gray-100">
                  <div className="w-8 h-8 rounded-lg bg-[#E8621A]/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-[#E8621A] text-sm"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#1A1A1A] truncate">
                      {selectedProject?.project_name || (projectLoading ? 'Loading project...' : 'Unknown project')}
                    </p>
                    {selectedProject?.project_code && (
                      <p className="text-[10px] text-[#9CA3AF]">{selectedProject.project_code}</p>
                    )}
                  </div>
                  <span className="text-[10px] font-bold text-[#E8621A] bg-[#FEF0E7] px-2 py-0.5 rounded-full flex-shrink-0">
                    {selectedProject?.status || '—'}
                  </span>
                </div>
              )}
            </div>

            {/* Project Summary Card */}
            {selectedProject && (
              <div className="bg-[#F9FAFB] rounded-xl p-3 border border-gray-100 space-y-1.5">
                <div className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-[#9CA3AF] text-xs"></i>
                  <span className="text-xs text-[#374151]">{selectedProject.site_address || 'No address'}</span>
                </div>
              </div>
            )}

            {/* Recipient Type Selector */}
            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">Send To</label>
              <div className="flex gap-2">
                {([
                  { value: 'internal' as RecipientType, label: 'Internal / Draft', icon: 'ri-draft-line' },
                  { value: 'subcontractor' as RecipientType, label: 'Subcontractor', icon: 'ri-tools-line' },
                ]).map((t) => (
                  <button
                    key={t.value}
                    onClick={() => {
                      setRecipientType(t.value);
                      if (t.value === 'subcontractor') {
                        if (subcontractors.length === 1) setToUserId(subcontractors[0].id);
                        else if (subcontractors.length === 0) setToUserId('');
                      } else {
                        setToUserId('');
                      }
                    }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[11px] font-semibold cursor-pointer whitespace-nowrap border-2 transition-colors ${
                      recipientType === t.value
                        ? 'border-[#E8621A] text-[#E8621A] bg-[#FEF0E7]/30'
                        : 'border-gray-100 text-[#374151] bg-gray-50'
                    }`}
                  >
                    <i className={`${t.icon} text-sm`}></i>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Context Block */}
            {selectedProject && (
              <div className="bg-[#1A1A1A] rounded-xl p-3 space-y-2">
                <p className="text-[10px] font-bold text-white/40 uppercase tracking-wider">Sending Context</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                  <div>
                    <p className="text-[10px] text-white/40">From</p>
                    <p className="text-xs font-semibold text-white">{senderName}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/40">Project</p>
                    <p className="text-xs font-semibold text-white truncate">{selectedProject.project_name}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/40">To</p>
                    <p className="text-xs font-semibold text-white truncate">{getRecipientContextDisplay()}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/40">Type</p>
                    <p className="text-xs font-semibold text-[#E8621A]">Instruction</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/40">Status</p>
                    <p className="text-xs font-semibold text-white">{getStatusContextDisplay()}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Subcontractor Recipient Detail */}
            {recipientType === 'subcontractor' && (
              <div className="space-y-2">
                <label className="block text-[#555555] text-xs font-semibold mb-1.5">Subcontractor Recipient</label>

                {!selectedProjectId ? (
                  <div className="bg-[#FEF2F2] rounded-xl p-3 border border-[#FECACA]">
                    <div className="flex items-start gap-2">
                      <i className="ri-error-warning-line text-[#EF4444] text-sm flex-shrink-0 mt-0.5"></i>
                      <p className="text-xs text-[#EF4444]">Please select a project first.</p>
                    </div>
                  </div>
                ) : subcontractorsLoading ? (
                  <div className="flex items-center gap-2 bg-[#F9FAFB] rounded-xl px-3 py-2.5 border border-gray-100">
                    <i className="ri-loader-4-line animate-spin text-[#9CA3AF] text-sm"></i>
                    <span className="text-sm text-[#9CA3AF]">Loading subcontractors...</span>
                  </div>
                ) : subcontractors.length === 0 ? (
                  <div className="space-y-3">
                    <div className="bg-[#FEF0E7] rounded-xl p-3 border border-[#FDBA74]">
                      <div className="flex items-start gap-2">
                        <i className="ri-information-line text-[#F59E0B] text-sm flex-shrink-0 mt-0.5"></i>
                        <div className="flex-1">
                          <p className="text-xs text-[#92400E] font-semibold mb-1">No subcontractors are assigned to this project yet.</p>
                          <p className="text-[10px] text-[#92400E]/80 mb-2">You can still save this as a Draft or Internal note. To send to a subcontractor, assign one first.</p>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => setShowAssignPanel(true)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E8621A] text-white text-[11px] font-bold cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-user-add-line"></i>
                              Assign Subcontractor
                            </button>
                            <button
                              onClick={() => setRecipientType('internal')}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-[#92400E] text-[11px] font-bold cursor-pointer whitespace-nowrap border border-[#FDBA74]"
                            >
                              <i className="ri-draft-line"></i>
                              Save as Draft
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    {showAssignPanel && (
                      <AssignSubcontractorPanel
                        projectId={selectedProjectId}
                        onAssigned={(sub) => {
                          setSubcontractors((prev) => [...prev, sub]);
                          setToUserId(sub.id);
                          setShowAssignPanel(false);
                        }}
                        onCancel={() => setShowAssignPanel(false)}
                      />
                    )}
                  </div>
                ) : subcontractors.length === 1 ? (
                  <div className="flex items-center gap-2 bg-[#ECFDF5] rounded-xl px-3 py-2.5 border border-[#A5F3FC]">
                    <i className="ri-tools-line text-[#059669] text-sm"></i>
                    <span className="text-sm font-medium text-[#059669]">
                      {subcontractors[0].full_name || subcontractors[0].company_name || subcontractors[0].email || 'Subcontractor'}
                    </span>
                    <button
                      onClick={() => setShowAssignPanel(true)}
                      className="ml-auto text-[10px] font-bold text-[#E8621A] cursor-pointer whitespace-nowrap"
                    >
                      + Add another
                    </button>
                    {showAssignPanel && (
                      <div className="w-full mt-2">
                        <AssignSubcontractorPanel
                          projectId={selectedProjectId}
                          onAssigned={(sub) => {
                            setSubcontractors((prev) => [...prev, sub]);
                            setToUserId(sub.id);
                            setShowAssignPanel(false);
                          }}
                          onCancel={() => setShowAssignPanel(false)}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <select
                      value={toUserId}
                      onChange={(e) => setToUserId(e.target.value)}
                      className="w-full min-w-0 max-w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] cursor-pointer"
                      style={{ boxSizing: 'border-box' }}
                    >
                      <option value="">Select subcontractor...</option>
                      {subcontractors.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.full_name || s.company_name || s.email || 'Unknown'}
                          {s.company_name && s.full_name ? ` (${s.company_name})` : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => setShowAssignPanel(true)}
                      className="text-[11px] font-bold text-[#E8621A] cursor-pointer whitespace-nowrap flex items-center gap-1"
                    >
                      <i className="ri-user-add-line"></i>Assign another subcontractor
                    </button>
                    {showAssignPanel && (
                      <div className="w-full">
                        <AssignSubcontractorPanel
                          projectId={selectedProjectId}
                          onAssigned={(sub) => {
                            setSubcontractors((prev) => [...prev, sub]);
                            setToUserId(sub.id);
                            setShowAssignPanel(false);
                          }}
                          onCancel={() => setShowAssignPanel(false)}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="space-y-3">
            <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">Details</p>

            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">Instruction Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Please prepare foundation formwork"
                className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[#555555] text-xs font-semibold mb-1.5">Priority</label>
                <div className="flex gap-2">
                  {[
                    { value: 'low', label: 'Low', color: '#9CA3AF' },
                    { value: 'medium', label: 'Medium', color: '#E8621A' },
                    { value: 'high', label: 'High', color: '#EF4444' },
                  ].map((p) => (
                    <button
                      key={p.value}
                      onClick={() => setPriority(p.value)}
                      className={`flex-1 py-2 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap border-2 transition-colors ${
                        priority === p.value ? 'border-current' : 'border-gray-100 bg-gray-50'
                      }`}
                      style={priority === p.value ? { borderColor: p.color, color: p.color, backgroundColor: `${p.color}10` } : {}}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[#555555] text-xs font-semibold mb-1.5">Due Date</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full min-w-0 bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] block"
                />
              </div>
            </div>

            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">Message *</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Describe what action you need from the subcontractor..."
                rows={4}
                maxLength={1000}
                className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] resize-none"
              />
              <div className="text-right text-[10px] text-[#9CA3AF] mt-0.5">{body.length}/1000</div>
            </div>
          </div>

          {/* Attachments */}
          <div className="space-y-2">
            <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">Attachments</p>
            {existingAttachments.filter((f) => !removedAttachmentIds.includes(f.id)).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {existingAttachments
                  .filter((f) => !removedAttachmentIds.includes(f.id))
                  .map((f) => (
                    <div key={f.id} className="relative w-20 h-20 rounded-xl overflow-hidden border border-gray-100">
                      <img src={f.file_url} alt={f.file_name} className="w-full h-full object-cover object-top" />
                      <button
                        onClick={() => setRemovedAttachmentIds((prev) => [...prev, f.id])}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center cursor-pointer"
                        title="Remove attachment"
                      >
                        <i className="ri-close-line text-white text-[10px]"></i>
                      </button>
                    </div>
                  ))}
              </div>
            )}
            {uploadedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {uploadedFiles.map((f) => (
                  <div key={f.id} className="relative w-20 h-20 rounded-xl overflow-hidden border border-gray-100">
                    <img src={f.file_url} alt={f.file_name} className="w-full h-full object-cover object-top" />
                    <button
                      onClick={() => setUploadedFiles((prev) => prev.filter((x) => x.id !== f.id))}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center cursor-pointer"
                    >
                      <i className="ri-close-line text-white text-[10px]"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={() => setShowFileUploader(true)}
              className="w-full py-2.5 rounded-xl border-2 border-dashed border-gray-200 text-[#9CA3AF] text-sm font-medium hover:border-[#E8621A] hover:text-[#E8621A] cursor-pointer transition-colors flex items-center justify-center gap-2"
            >
              <i className="ri-camera-line"></i>
              {uploadedFiles.length > 0 ? `${uploadedFiles.length} photos attached — Add more` : 'Attach Photos / Files'}
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 bg-[#FEF2F2] rounded-xl p-3">
              <i className="ri-error-warning-line text-[#EF4444] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-[#EF4444] text-xs">{error}</p>
            </div>
          )}
        </div>

        {/* Submit */}
        <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0 space-y-2">
          <button
            onClick={handleSubmit}
            disabled={saving || !isFormValid}
            className="w-full py-3.5 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <><i className="ri-loader-4-line animate-spin"></i>Saving...</>
            ) : (
              <>
                <i className={recipientType === 'internal' ? 'ri-draft-line' : 'ri-send-plane-line'}></i>
                {editingInstruction
                  ? 'Save Changes'
                  : recipientType === 'internal'
                    ? 'Save as Draft'
                    : 'Send Instruction'}
              </>
            )}
          </button>
          {!isFormValid && getValidationMessage && (
            <p className="text-center text-[10px] text-[#9CA3AF] mt-2">{getValidationMessage}</p>
          )}
        </div>
      </div>

      {/* File Uploader */}
      {showFileUploader && user && selectedProjectId && (
        <FileUploader
          projectId={selectedProjectId}
          currentUserId={user.id}
          open={showFileUploader}
          onClose={() => setShowFileUploader(false)}
          onUploadComplete={handleFileUploadComplete}
          accountType="management"
          category="photo"
          allowMultiple={true}
          allowCamera={true}
          enablePerFileCaption={true}
          bucket="instruction-photos"
          relatedType="instruction"
          title="Attach Photos"
        />
      )}
    </div>
  );
}